import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lightbox } from "@/components/hub/shared/lightbox";
import { useLightbox } from "@/components/hub/shared/use-lightbox";
import { InterestImage } from "@/lib/api/interest-images";
import {
  useAddInterestImage,
  useRemoveInterestImage,
} from "@/lib/hooks/interest-images/use-interest-images";
import { ImagePlus, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/components/ui/use-toast";

const MAX_IMAGES_PER_INTEREST = 3;
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB
const ACCEPTED_IMAGE_TYPES = /^image\/(jpeg|png|webp|gif)$/;

interface InterestImagesManagerProps {
  userId: string;
  interests: string[];
  interestImages: InterestImage[];
}

interface InterestRowProps {
  userId: string;
  interest: string;
  images: InterestImage[];
}

const normalizeImageUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url;
  }
};

function InterestRow({ userId, interest, images }: InterestRowProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [removingImageUrls, setRemovingImageUrls] = useState<string[]>([]);
  const [stableImageUrls, setStableImageUrls] = useState<
    Record<string, string>
  >({});

  const addImage = useAddInterestImage(userId);
  const removeImage = useRemoveInterestImage(userId);

  const interestImages = useMemo(
    () => images.filter((img) => img.interest === interest),
    [images, interest],
  );
  const interestImageEntries = useMemo(
    () =>
      interestImages.map((img) => ({
        key: normalizeImageUrl(img.imageUrl),
        url: img.imageUrl,
      })),
    [interestImages],
  );
  const lightbox = useLightbox(interestImageEntries.length);
  const lightboxImages = useMemo(
    () =>
      interestImageEntries.map(({ key, url }) => ({
        url: stableImageUrls[key] ?? url,
        alt: `${interest} photo`,
      })),
    [interestImageEntries, stableImageUrls, interest],
  );

  useEffect(() => {
    setStableImageUrls((prev) => {
      const next: Record<string, string> = {};
      interestImageEntries.forEach(({ key, url }) => {
        next[key] = prev[key] ?? url;
      });
      return next;
    });
  }, [interestImageEntries]);

  const remainingSlots = MAX_IMAGES_PER_INTEREST - interestImages.length;
  const canAddMore = remainingSlots > 0;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0) return;

    // Reset file input so the same file can be selected again
    event.target.value = "";

    // Validate file types
    const invalidTypeFiles = selectedFiles.filter(
      (file) => !ACCEPTED_IMAGE_TYPES.test(file.type),
    );
    if (invalidTypeFiles.length > 0) {
      toast({
        title: "Invalid file type",
        description: "Please upload JPEG, PNG, WebP, or GIF images only.",
        variant: "destructive",
      });
      return;
    }

    // Validate file sizes
    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > MAX_FILE_SIZE_BYTES,
    );
    if (oversizedFiles.length > 0) {
      toast({
        title: "File too large",
        description: `Each image must be under 15 MB.`,
        variant: "destructive",
      });
      return;
    }

    const filesToUpload = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      toast({
        title: "Some images skipped",
        description: `You can upload up to ${MAX_IMAGES_PER_INTEREST} photos per interest.`,
      });
    }

    setIsUploading(true);
    setUploadingCount(filesToUpload.length);
    try {
      const results = await Promise.allSettled(
        filesToUpload.map((file) => addImage.mutateAsync({ interest, file })),
      );
      const successCount = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failureCount = results.length - successCount;

      if (successCount > 0) {
        toast({
          title: "Images uploaded",
          description: `${successCount} photo${successCount > 1 ? "s" : ""} added for "${interest}"`,
        });
      }

      if (failureCount > 0) {
        toast({
          title: "Some uploads failed",
          description: `${failureCount} photo${failureCount > 1 ? "s" : ""} could not be uploaded.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to upload interest image:", error);
      toast({
        title: "Upload failed",
        description: "Could not upload the photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadingCount(0);
    }
  };

  const handleRemove = async (imageUrl: string) => {
    const normalizedUrl = normalizeImageUrl(imageUrl);
    setRemovingImageUrls((prev) =>
      prev.includes(imageUrl) ? prev : [...prev, imageUrl],
    );
    try {
      await removeImage.mutateAsync({ interest, imageUrl: normalizedUrl });
      toast({
        title: "Image removed",
        description: `Photo removed from "${interest}"`,
      });
    } catch (error) {
      console.error("Failed to remove interest image:", error);
      toast({
        title: "Remove failed",
        description: "Could not remove the photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRemovingImageUrls((prev) => prev.filter((url) => url !== imageUrl));
    }
  };

  return (
    <Card className="p-4">
      <h4 className="mb-3 font-medium capitalize">{interest}</h4>

      <div className="flex flex-wrap gap-3">
        {/* Existing images */}
        {interestImageEntries.map(({ key, url }) => (
          <div key={key} className="group relative">
            <button
              type="button"
              className="block"
              onClick={() => {
                const index = interestImageEntries.findIndex(
                  (entry) => entry.key === key,
                );
                if (index >= 0) lightbox.open(index);
              }}
              aria-label={`Open ${interest} photo`}
            >
              <img
                src={stableImageUrls[key] ?? url}
                alt={`${interest} photo`}
                className="h-24 w-24 rounded-lg border object-cover"
              />
            </button>
            <button
              onClick={() => handleRemove(url)}
              disabled={removingImageUrls.includes(url)}
              aria-label={`Remove photo from ${interest}`}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 transition-opacity disabled:cursor-not-allowed disabled:opacity-50 group-hover:opacity-100"
            >
              {removingImageUrls.includes(url) ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <X className="h-3 w-3" />
              )}
            </button>
          </div>
        ))}

        {uploadingCount > 0 &&
          Array.from({ length: uploadingCount }).map((_, index) => (
            <div
              key={`uploading-${interest}-${index}`}
              className="flex h-24 w-24 items-center justify-center rounded-lg border border-dashed"
            >
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ))}

        {/* Upload button */}
        {canAddMore && !isUploading && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-24 w-24 flex-col gap-1 rounded-lg border-dashed"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <>
                <ImagePlus className="h-5 w-5" />
                <span className="text-xs">Add photos</span>
              </>
            </Button>
          </div>
        )}

        {/* Empty state for this interest */}
        {interestImages.length === 0 && !isUploading && (
          <p className="text-sm text-muted-foreground">No photos yet.</p>
        )}
      </div>

      <p className="mt-2 text-xs text-muted-foreground">
        {interestImages.length}/{MAX_IMAGES_PER_INTEREST} photos
      </p>

      {lightbox.isOpen && lightboxImages[lightbox.currentIndex] && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.close}
          onPrev={lightbox.prev}
          onNext={lightbox.next}
          header={<span className="text-sm font-medium capitalize">{interest}</span>}
        />
      )}
    </Card>
  );
}

export function InterestImagesManager({
  userId,
  interests,
  interestImages,
}: InterestImagesManagerProps) {
  if (interests.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">
          This mentor has no interests set yet.
        </p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
      {interests.map((interest) => (
        <InterestRow
          key={interest}
          userId={userId}
          interest={interest}
          images={interestImages}
        />
      ))}
    </div>
  );
}